/**
 * @fileoverview Supply operator-facing descriptions keyed to backend scheduled-job identity. `ScheduledJobs.js` renders this content beside live configuration and run observations. Descriptions explain intent and selected actions; they do not define execution policy or establish current activation. Keep copy aligned with the owning job implementation.
 */




const JOB_DOCS = {
    app_ai_spend_alerts: `
## App AI spending alerts

Sends Kevin Van Dyke a Slack DM every five minutes once the daily app AI budget reaches $5, followed by one final hard-stop notice at $10. No further spending DMs are sent for that Pacific day. The provider-call guard blocks requests that cannot fit within the $10 cap, including concurrent reservations. Days reset at midnight Pacific.

Alerts also show provider-reported organization costs for the current UTC day. These reports can lag and include other applications; they are separate from the app budget. The ledger conservatively accounts for model tokens and web-search fees. Failed or interrupted requests retain their reserved budget. Turning alerts off does not disable the $10 cap; turning alerts on does not enable research or the intake rollout.
`,
    work_comparison_refresh: `
## Time & Work
Keeps Trello history and daily employee totals in the database so the overview does not contact Trello.
Document counts are filtered by the viewer's current access on every read.

Disabled by default. Requires WORK_COMPARISON_REFRESH_ENABLED=true and, in production,
an explicit WORK_COMPARISON_REFRESH_EMPLOYEE_IDS allowlist.
The recurring 15-minute schedule separately requires WORK_COMPARISON_REFRESH_SCHEDULE_ENABLED=true.
WORK_COMPARISON_REFRESH_MAX_CALLS limits each run to 1–20 Trello reads (default 5).
There are no model calls, external writes, or automatic retries.
Actual call counts and zero model spend are recorded in the refresh audit history.
The job toggle or WORK_COMPARISON_REFRESH_STOP=true stops queued work and prevents publication.
Incomplete refreshes retain the previous snapshot. Older corrections require a selected manual refresh.
`,
    portal_result_notifications: `
## Client portal results
Delivers committed portal submissions to the employee selected in Settings → Client Portal.
Runs every minute only when PORTAL_NOTIFICATIONS_ENABLED=true in production.
Sandbox submissions never send. Failed deliveries retry with backoff; successful deliveries are recorded.
The notification links to read-only results and creates no Inbox tasks. It does not approve or send payment.
`,
    ai_calendar_gmail_polling: `
## Overview
Polls Gmail for unread emails sent to **deadlines@kneuppercovey.com** and queues them for AI-powered deadline extraction. This is the entry point of the AI Calendar pipeline.

## How It Works
1. Fetches up to **50 unread messages** per poll
2. Deduplicates against previously processed message IDs
3. Validates sender domain — only \`@kneuppercovey.com\` and specific allowlisted addresses
4. Marks each email as read immediately to prevent re-queuing
5. Queues each message for the AI deadline extraction pipeline via Celery

## Services
- **Gmail API** — read unread emails, mark as read
- **Celery** — async task queuing for downstream processing

## Notes
- Individual email failures don't stop the batch — other emails continue processing
- Max 3 retries with 60-second delays on failure
- Downstream pipeline extracts deadlines from attachments and creates calendar events
`,

    email_activity_import: `
## Overview
Imports email communication history from all Google Workspace mailboxes into the Activity log by scanning for client and opposing counsel messages linked to matters.

## How It Works
1. Loads timestamp of last successful run for incremental import
2. Discovers all Workspace mailboxes via Google Directory API
3. Loads all tracked external email addresses from the database (clients plus optional matter opposing counsel), normalizes them, and skips firm-domain addresses from backfill
4. **Incremental phase** — for each mailbox, fetches messages since last run, matches against tracked external emails, and creates Activity records
5. **Backfill phase** — for newly discovered tracked external emails, performs a batched full-history scan across all mailboxes
6. Records new checkpoint timestamp for next run

## Services
- **Gmail API** — list and fetch messages with headers
- **Google Directory API** — enumerate Workspace users

## Activity Records
Each record captures: date, client contact, attorney contact, subject, matter number, and direction (inbound/outbound). Deduplication prevents duplicate entries on re-runs.

## Notes
- Internal firm-to-firm emails are skipped
- Firm-domain client addresses are excluded from full-history backfill to avoid runaway internal scans
- New-client backfills are batched across runs to keep the scheduled job bounded
- Gmail retry windows are honored before Celery retries
    - Supports pagination for large result sets (up to 500 per query)
    `,

    pipedrive_intake_leads_import: `
## Overview
Manual repair backfill for Pipedrive Leads Inbox records. Normal Intake ingestion now comes from Pipedrive webhooks, not scheduled polling.

## How It Works
1. Fetches recent leads from Pipedrive using \`GET /v1/leads\`
2. Creates or updates matching Intake leads by Pipedrive lead UUID
3. Stores each raw Pipedrive payload as an Intake source event for audit and idempotency
4. Normalizes client email, phone, and name
5. Writes duplicate candidate rows for human review
6. Leaves stage execution to the separate Intake parallel automation job

## Services
- **Pipedrive API** — Leads Inbox records
- **Intake** — lead records, source events, and duplicate candidates

## Notes
- This job is manual-only and should be used for repair/backfill
- Live lead create/change events are handled by the Webhooks tab
- It does not send outreach, reject leads, or convert matters
- Human confirmation is required before any duplicate merge
`,

    intake_parallel_pipeline_automation: `
## Overview
Prepares active Intake leads for batch scoring, then advances promoted leads toward the information-request draft. It does not score closed batches and does not send client email.

## How It Works
1. Finds active Intake leads that are not converted, archived, rejected, or duplicates
2. Skips leads with active stage/agent/research work already running
3. Stops on duplicate review or any staff-review blocker
4. Starts the next configured Intake stage in order, except that closed-batch triage is owned by the separate Batch Scoring job
5. When an information-request draft exists, marks the lead as needing staff action with \`Review and send information request email\`

## Services
- **Intake pipeline** — stage runs and agent runs
- **OpenAI** — only through normal configured Intake agents

## Notes
- Email sending is deliberately manual
- Scheduled runs use a small batch limit and skip if another automation pass is still active
- This is intended for quiet first deployment beside the team's existing Pipedrive process
- Leads should pile up at the draft-send action until staff chooses to send
`,

    intake_batch_scoring_automation: `
## Overview
Prepares and scores every eligible lead in a closed Intake promotion batch before ranking or promoting that batch.

## How It Works
1. Closes open promotion batches when they reach four days or the configured lead limit
2. Finishes the oldest closed batch first and finds members that are unscored or use an older ranking contract
3. Runs any configured pre-triage prerequisite, including Ingest and Dedupe
4. Automatically continues each completed prerequisite into the configured triage stage
5. Requires five merit components, calculates the weighted score on the server, and stores the component breakdown and rationale
6. Automatically retries incomplete or invalid scoring output up to a bounded attempt limit
7. Waits until every non-terminal member has a current-contract score, then breaks equal totals by merit, recovery, firm fit, defendant quality, and completeness
8. Promotes only above-threshold members within the configured outreach cap; the rest remain held with an explicit batch status

## Services
- **Intake pipeline** — triage stage and agent execution
- **Intake promotion batches** — score, rationale, rank, and promotion state
- **OpenAI** — only through the configured triage agent

## Notes
- Active class-action leads bypass ordinary batch scoring
- A lead needing basic facts can hold the batch until it is scored or reaches a terminal outcome
- Scheduled runs are bounded and resume remaining members on the next cadence
- Arrival time is only the final deterministic fallback after the full merit vector ties
- Outreach remains a separate, human-reviewed action
`,

    intake_reply_monitor: `
## Overview
Monitors **intake@kneuppercovey.com** for prospect replies to sent Intake information-request emails.

## How It Works
1. Reads recent messages from the Intake mailbox on a scheduled cadence
2. Deduplicates by Gmail message ID in \`IntakeInboundReplies\`
3. Matches replies to sent Intake outreach by Gmail thread, falling back to prospect email and sent timestamp
4. Routes normal replies to prospect-response review
5. Sends one confidentiality reassurance when a prospect first asks to speak with an attorney
6. Marks staff review needed if the prospect asks for an attorney again after the reassurance

## Services
- **Gmail API** — read Intake mailbox replies
- **SMTP Intake mailer** — sends the confidentiality reassurance from the Intake mailbox
- **Intake workflow timers** — supersedes no-response timers and schedules a fresh window after the reassurance

## Notes
- Duplicate monitor runs do not resend reassurance emails
- Gmail/config errors are reported in the job result without blocking the separate Intake pipeline automation
`,

    intake_won_deal_sync: `
## Overview
Manual repair backfill for Intake leads that may have missed a Pipedrive deal-won webhook.

## How It Works
1. Finds active Intake leads with a Pipedrive lead or deal id
2. Re-ingests converted Pipedrive leads lightly when needed to discover the deal id
3. Fetches linked Pipedrive deals and checks for \`status = won\` or a won timestamp
4. Creates a client-won event when a won deal is found
5. Queues the normal client-won processing workflow
6. If the client-won event already created the matter, hydrates and archives the matching Intake lead

## Services
- **Pipedrive API** — deal and converted-lead lookup
- **Client Won workflow** — matter creation and onboarding
- **Intake** — local lead linking, archival, and activity hydration

## Notes
- This is no longer scheduled; deal-won processing is webhook-driven
- Use this only when repairing missed webhook delivery or historical data
- It does not create matters directly; it hands off to the existing client-won path
`,

    intake_workflow_timer_sweep: `
## Overview
Processes due Intake workflow timers from the database so missed ETA timer tasks are recovered automatically.

## How It Works
1. Runs on a short scheduled cadence
2. Finds pending Intake workflow timers whose due time has elapsed
3. Claims each timer before handling it so overlapping runs do not double-process the same timer
4. Applies the same timer handlers used by one-off ETA timer tasks
5. Skips closed or converted leads without changing their workflow state

## Services
- **Intake workflow timers** — pending no-response, reminder, archive-review, and debounce timers
- **Celery beat** — periodic recovery sweep
- **Redis** — scheduled-run lock when available

## Notes
- This is a durability sweep; normal ETA timer tasks can still fire first
- Manual runs are useful after worker downtime or deploy interruptions
- The job does not send email by itself
`,

    pipedrive_intake_file_sync: `
## Overview
Manual repair backfill for Pipedrive files. Normal Intake file discovery now runs from related webhook events and custom file webhooks.

## How It Works
1. Reads recent Pipedrive file changes from \`/v1/recents?items=file\`
2. Matches file events to Intake leads by Pipedrive lead/person IDs
3. Finds or creates the client's Drive folder using \`Client Name - PersonID\`
4. Copies unseen Pipedrive files into Google Drive
5. Stores Drive-backed Intake document rows and a Pipedrive file sync ledger
6. Runs a rotating backstop batch so older open leads are rechecked over time

## Services
- **Pipedrive API** — file metadata and downloads
- **Google Drive API** — potential-client folders and copied documents
- **Intake** — document rows, sync checkpoint, and file ledger

## Notes
- Pipedrive download URLs are not used as Intake document links
- The ledger prevents duplicate copies across import, scheduled sync, and retries
- This job is manual-only because Pipedrive does not guarantee a first-class file webhook
`,

    email_activity_prune: `
## Overview
Prunes hot copies of full email bodies, raw MIME, and attachments for Activity-email records only after every linked matter has been withdrawn or fully resolved for more than **60 days**.

## How It Works
1. Loads stored Activity email messages that still have hot content
2. Finds every matter linked to each stored Gmail message
3. Confirms all linked matters are currently:
   - \`Withdrawn\`, or
   - \`Settled/Resolved → Payments Complete/Fully Resolved\`
4. Derives the qualifying transition timestamp from existing \`StageChanged\` / \`SubstageChanged\` Activity rows
5. Deletes the hot copies of raw MIME and attachment binaries, nulls stored bodies, and marks the stored message as pruned
6. Keeps message metadata, attachment metadata, and timeline links intact so the Activity pane can show a placeholder state

## Services
- **MySQL / ActivityEmailMessages + ActivityEmailAttachments** — metadata and prune state
- **Object storage or local disk** — hot body/attachment binaries

## Notes
- A message is pruned only when **all** linked matters are eligible
- If a matter is currently in a prunable state but the app cannot derive the qualifying transition date, the message is skipped rather than pruned
`,

    email_activity_enrichment_backfill: `
## Overview
One-time repair job that enriches existing Activity email rows with full stored email bodies and attachments, but only for matters that are **not currently prune-eligible**.

## How It Works
1. Finds legacy \`ActivityType = Email\` rows that do not yet point to a stored Gmail message
2. Skips matters already prune-eligible under the retention policy
3. Searches the attorney mailbox in Gmail using the existing activity timestamp window, subject, direction, and external contact
4. Requires a **unique** Gmail match after exact subject/contact/date verification
5. Stores the full email body, raw MIME, and attachments once per Gmail message
6. Links the existing Activity row to that stored email record without creating a new timeline entry

## Services
- **Gmail API** — search, fetch, and download matched messages and attachments
- **MySQL / Activity + ActivityEmailMessages** — link legacy rows to stored content

## Notes
- Ambiguous or missing matches are skipped, not guessed
- This job is manual-only because it is intended as a rollout repair/backfill tool
`,

    trello_queue_processing: `
## Overview
Processes pending tasks from the internal TaskQueue by creating Trello cards when a task's scheduled date arrives.

## How It Works
1. Queries TaskQueue for unprocessed tasks where the scheduled date has passed
2. For each task, creates a Trello card with the task's name, details, labels, member assignment, and due date
3. Marks the task as processed so it won't be picked up again

## Services
- **Trello API** — create cards on the firm's board

## Card Details
- **Name:** task name from queue
- **Description:** task details
- **Assigned to:** mapped Trello member (if specified)
- **Labels:** from task configuration
- **Due date:** task deadline (if specified)

## Notes
- Individual card creation failures don't block other tasks in the queue
`,

    banking_client_lookup_check: `
## Overview
Retries client lookups for banking submissions that couldn't initially match a client in the database or Pipedrive. Runs hourly to allow time for clients to be added to the system.

## How It Works
1. Finds all submissions with status \`pending_client_lookup\`
2. For each submission:
   - Searches the **Clients table** by email (primary and additional)
   - If not found, searches **Pipedrive API** by email
   - On match: resolves jurisdiction and primary attorney, creates a Trello verification card, and notifies the attorney via Slack DM
   - On timeout (72 attempts / 3 days): marks as \`timeout_client_not_found\` and alerts the Cyclone user

## Services
- **Pipedrive API** — person search by email
- **Trello API** — create verification card
- **Slack API** — DM attorney + timeout alerts

## Timeout
After **72 hours** without a match, the submission is marked timed out and a Slack alert is sent.

## Notes
- Scheduled runs are offset from the main Intake/Pipedrive jobs and skip if a prior lookup pass is still active
`,

    banking_verification_check: `
## Overview
Monitors pending banking verifications by polling their Trello cards. When a card is moved to the "Done" list, it triggers completion notifications.

## How It Works
1. Finds all submissions with status \`pending_verification\`
2. For each submission, checks if its Trello verification card has been moved to the "Done" list
3. On verification complete:
   - Updates status to \`verified\` with timestamp
   - Pulls all comments from the Trello card
   - Sends detailed notification to the **accounting channel** with banking details and card comments
   - Sends notification to the **assigned attorney** via Slack DM
4. On timeout (144 attempts / 6 days): marks as \`timeout_verification\` and alerts

## Services
- **Trello API** — get card status, board lists, card comments
- **Slack API** — channel messages and DMs

## Notifications
| Recipient | Trigger |
|-----------|---------|
| Accounting channel | Verification complete — full banking details + Trello comments |
| Assigned attorney | Verification complete — banking details |
| Cyclone user | Timeout after 6 days |
`,

    xero_journal_sync: `
## What it does
Imports cash-basis journals, invoices, and bills every 20 minutes. After a complete historical scan, fills resolved-case financial attributes from Xero while preserving manual values.

## Recovery and matching
- Journal-number checkpoints resume bounded historical imports, including reversals.
- Invoice pages and their checkpoints commit together; incremental scans overlap one day.
- Invoice balances require a single unambiguous matter; other mappings remain flagged.
- Completion events refresh resolved cases immediately from the cache. Each import also sweeps resolved cases.
- Unknown or conflicting financial amounts remain flagged for review. No missing amount is assumed to be zero.
- Only the backend owns OAuth refresh after cutover; n8n Xero importers must remain inactive.
`,

    xero_payment_settlement_check: `
## Overview
Scans newly arrived Xero journal rows for settlement-payment receipts and asks the primary attorney in Slack whether the matter should move to Settled/Resolved.

## How It Works
1. Establishes a one-time baseline cursor on first run so historical Xero journals do **not** trigger retroactive prompts
2. Reads only journal rows added after the saved cursor
3. Groups rows by \`JournalID\` and looks for settlement-payment journal shapes:
   - \`SourceType = CASHREC\`
   - a positive \`BANK\` amount
   - at least one description containing \`settlement fund\`
4. Enqueues one durable prompt record per matching journal ID
5. Sends a Slack DM to the matter's primary attorney with settlement substage buttons
6. When the attorney clicks a button, the matter moves to Stage 10 and the Xero prompt record is marked confirmed

## Services
- **MySQL / XeroJournals** — settlement-payment signal source
- **Slack API** — attorney DM and confirmation interaction

## Notes
- This job is the settlement trigger now; banking verification no longer prompts settlement
- One prompt is sent per Xero \`JournalID\`
- Journals for already-settled matters are skipped
`,

    jams_document_retrieval: `
## Overview
Scans attorney inboxes for JAMS (Judicial Arbitration and Mediation Services) notification emails, downloads linked documents from the JAMS portal, and delivers them via Slack. Also feeds documents into the AI Calendar pipeline for deadline extraction.

## How It Works
1. Finds all active attorneys with stored JAMS credentials
2. For each attorney, scans their Gmail (via service account delegation) for emails from \`jamsadr.com\` or \`jamsaccess.com\` in the last 2 days
3. For each unprocessed email:
   - Validates sender domain
   - Parses email body for JAMS document URLs
   - Logs into the JAMS portal using the attorney's credentials
   - Downloads documents (handles ZIP files containing multiple documents)
   - Uploads each file to the attorney's **Slack DM**
   - Feeds documents into the **AI Calendar pipeline** for deadline extraction (non-blocking)
4. Marks processed emails as read

## Services
- **Gmail API** — list, fetch, and mark messages as read
- **JAMS Portal** — login and document download
- **Slack API** — file upload to attorney DMs
- **AI Calendar Pipeline** — deadline extraction (optional, non-blocking)

## Supported File Types
PDF, DOCX, DOC

## Notes
- Per-attorney failures don't block other attorneys
- AI pipeline failures don't prevent Slack delivery
- Gmail search window: last 2 days, max 20 results per attorney
`,

    trello_card_hygiene_check: `
## Overview
Monitors open Trello cards for missing required fields (members and/or labels) and DMs card creators asking them to fill in the information.

## How It Works
1. Fetches all open cards on open (non-archived) lists
2. Identifies the creator of each card from board action history
3. Filters out template cards and cards younger than **15 minutes** (grace period)
4. Flags cards missing members or labels
5. Groups flagged cards by creator
6. Sends each creator a **Slack DM** listing their cards and what's missing

## Services
- **Trello API** — get open lists, cards, and board actions
- **Slack API** — DMs to card creators

## Slack Message
Block Kit message with header "Trello Card Hygiene Check" listing each card with a link and what it's missing (members, labels, or both).

## Notes
- Creator-to-Slack mapping comes from the Employees table (TrelloMemberId → SlackUserId)
- Creators without a Slack mapping are skipped with a log message
`,

    trello_alyssa_nag: `
## Overview
Sends escalating Slack reminders to Alyssa about her assigned Trello cards that have been open longer than 3 days.

## How It Works
1. Looks up Alyssa in the Employees table to get her Trello and Slack IDs
2. Fetches all open cards and filters for those assigned to Alyssa
3. Calculates each card's age from its creation timestamp
4. Filters for cards older than **3 days**
5. Sorts by age (oldest first) and sends a single Slack DM

## Escalation Levels
| Card Age | Tone |
|----------|------|
| 3–5 days | Gentle reminder |
| 5–7 days | Follow-up |
| 7–10 days | Needs attention |
| 10–14 days | Overdue |
| 14+ days | Requires immediate action |

The overall message tone matches the **oldest** card in the batch.

## Services
- **Trello API** — get open cards
- **Slack API** — DM to Alyssa
`,

    notification_rules: `
## Overview
Evaluates configurable notification rules to identify matters stuck in a stage/substage too long or missing required case attributes, then sends Slack alerts to attorneys and channels.

## Rule Types

### Duration Rules
Find matters that have been in a particular stage or substage longer than a configured number of days.

### Attribute Rules
Find matters missing required case attributes (e.g., Jurisdiction, Case Number) after being in a stage for a configured number of days.

## How It Works
1. Loads all active notification rules from the database
2. For each **duration rule**: queries for matters where the most recent stage change is older than the threshold
3. For each **attribute rule**: queries for matters missing any of the required attributes past the threshold
4. Groups matched matters by attorney
5. Sends notifications:
   - **Channel message** (if rule has a configured Slack channel) with grouped summary
   - **DM to each attorney** with their individual matters

## Deduplication
Each triggered rule creates a \`RuleMatched\` or \`AttributeRuleMatched\` Activity record per matter, preventing repeat notifications for the same match.

## Services
- **Slack API** — channel messages and DMs (with email-to-Slack-ID caching)

## Notes
- Rules are managed in the Admin > Notification Rules section
- Slack user lookups are cached per evaluation run for efficiency
- Matters without a primary client are excluded
`,
    google_calendar_sync: `
## Overview
Detects events deleted on Google Calendar and syncs RSVP (accept/decline) status changes back to the internal calendar.

## How it works
1. Uses Google's **incremental sync token** to fetch only events that changed since the last run
2. If a Google event has \`status=cancelled\`, the corresponding internal event is soft-deleted
3. If all attendees have accepted, the internal event is marked as **confirmed**
4. The sync token is stored in Redis between runs

## Services
- **Google Calendar API** — via service account (domain-wide delegation)
- **Redis** — stores the sync token between runs

## Notes
- On first run (or token expiry), performs a full sync to seed the token
- Also triggered by Google Calendar push webhooks when configured
`,
    google_calendar_backfill: `
## Overview
Finds future internal calendar events that are missing from Google Calendar and creates them. This recovers from transient Google API failures that occurred when the event was originally created.

## How it works
1. Queries for non-deleted future events where \`google_event_id\` is NULL
2. For each event, builds the Google Calendar event body (with attendees) and creates it via the API
3. Stores the returned \`google_event_id\` back in the database
4. Skips events with invalid dates (e.g. start > end)

## Services
- **Google Calendar API** — via service account (domain-wide delegation)

## Notes
- Uses \`sendUpdates='all'\` so attendees receive calendar invitations
- Events with inverted dates are logged as warnings and skipped
`,

    clockify_sync: `
## Overview
Incrementally syncs Clockify time entries and projects into the local database, and refreshes \`AttorneyRates\` from Clockify workspace user hourly rates. Replaces the n8n webhook-based sync with a polling approach that fetches entries modified since the last run.

## How It Works
1. Reads the last successful sync timestamp from Redis
2. Fetches all workspace users from the Clockify API
3. For each user, fetches time entries started since last sync (with 5-minute overlap buffer)
4. Upserts entries into the ClockifyTimeEntries table (insert or update by Clockify ID)
5. Syncs all Clockify projects into the ClockifyProjects table
6. Upserts \`AttorneyRates\` from Clockify workspace user hourly rates without deleting unrelated local rows
7. Every 4th run (~1 hour), performs a deletion reconciliation to detect and remove entries deleted from Clockify

## Services
- **Clockify API** — workspace users, time entries, projects, and workspace user hourly rates
- **Redis** — stores last-sync timestamp and reconciliation cycle counter

## Deletion Handling
Every ~1 hour, the task fetches time entry IDs from the last 30 days in Clockify and compares against the local table. Entries present locally but missing from Clockify are deleted.

## Notes
- Rate limited (150ms between API calls) to stay within Clockify's 10 req/sec limit
- If Redis checkpoint is missing (first run or Redis flush), falls back to syncing the last 24 hours
- Upserts are idempotent — safe to re-run or overlap time windows
- Clockify is the source of truth for synced hourly rates; manual edits in \`AttorneyRates\` may be overwritten on the next run
`,

    attorney_billing_inactivity_alert: `
## Overview
Checks active attorneys for recent billed time and sends a Slack alert when an attorney has not billed in 4 or more days.

## How It Works
1. Loads active attorneys from the internal database
2. Normalizes attorney email addresses and matches them to \`Employees.Email\`
3. Resolves each matched employee to \`Employees.ClockifyUserID\`
4. Looks up the latest \`ClockifyTimeEntries.EndTime\` by Clockify user id
5. Sends one Slack message per qualifying attorney to the configured billing oversight destination

## Services
- **MySQL** — attorneys, employees, and synced Clockify time entries
- **Slack API** — sends the alert message

## Notes
- Uses email-based identity resolution instead of attorney display names
- Attorneys missing an email, employee mapping, or Clockify user id are skipped and logged
- Manual runs return summary counts for evaluated, alerted, and skipped attorneys
`,

    client_contact_reminders: `
## Overview
Tracks stale outbound communication by matter and sends Slack reminders when an active matter has gone 30 calendar days without a qualifying outbound communication activity.

## How It Works
1. Loads all non-hidden matters except \`Settled/Resolved\` and \`Withdrawn\`
2. Finds the latest outbound \`Email\`, \`TextMessage\`, \`PhoneCall - Answered\`, or \`PhoneCall - Not Answered\` on the same \`MatterNumber\`
3. Falls back to \`Matters.DateEngaged\` when the matter has never had a qualifying outbound communication
4. Maintains one reminder-state row per matter in \`MatterContactReminders\`
5. Suppresses the first-seen historical backlog instead of blasting old matters on rollout
6. Sends due reminders every 15 minutes during the business-hours window, repeating every 3 days while the same stale cycle remains active

## Reminder Rules
- First reminder: **30 calendar days** after the most recent qualifying outbound communication, or \`DateEngaged\` if none exists
- Follow-up reminders: **every 3 days**
- Dispatch window: **weekdays, 9:00 AM to 5:00 PM Central**

## Recipient Logic
- Uses the matter's primary active attorney, or the first active attorney when no primary flag is present
- Falls back to the configured admin Slack target when no attorney destination can be found
- Dashboard refreshes are read-only and do not send Slack or mutate reminder state

## Notes
- Only communication rows already tied to a \`MatterNumber\` count for that matter; null-matter activities are ignored
- Hidden, settled, and withdrawn matters are excluded
- The Automations dashboard shows current offending matters, baseline-suppressed backlog rows, and missing-recipient cases
`,

    production_health_monitor: `
## Overview
Checks critical production dependencies every 15 minutes and posts Slack alerts when a dependency first fails or recovers.

## How It Works
1. Runs read-only checks for database, Redis, MongoDB, Celery worker connectivity, scheduled-job freshness, and Slack alert configuration
2. Writes each check result to SystemLogs with \`name=monitoring_check\`
3. Compares each check to its previous result
4. Sends Slack only on state changes:
   - success → error: failure alert
   - error → success: recovery alert

## Services
- **Database** — \`SELECT 1\`
- **Redis** — \`PING\`
- **MongoDB** — admin \`ping\` when Mongo is configured
- **Celery** — worker inspect ping
- **Slack API** — alerts to \`MONITORING_SLACK_CHANNEL\`, falling back to \`SLACK_ERROR_CHANNEL\`

## Notes
- This internal monitor cannot detect total droplet, network, or Celery Beat failure by itself. Pair it with an external uptime/heartbeat monitor.
- Alerts are stateful to avoid repeating the same failure every 15 minutes.
`,
};

export default JOB_DOCS;
